/**
 * API Scopes, Scope Presets, and MCP Tool Manifest
 *
 * Defines the permission scopes for API key access control and maps
 * MCP tools to their required scopes.
 */

// ============================================================================
// API SCOPES
// ============================================================================

/**
 * Individual API scopes grouped by domain.
 * Each domain has read and write variants.
 */
export const API_SCOPES = {
  Cases: {
    READ: 'cases:read',
    WRITE: 'cases:write',
  },
  Consumers: {
    READ: 'consumers:read',
    WRITE: 'consumers:write',
  },
  Documents: {
    READ: 'documents:read',
    WRITE: 'documents:write',
  },
  Tasks: {
    READ: 'tasks:read',
    WRITE: 'tasks:write',
  },
  Agents: {
    READ: 'agents:read',
    WRITE: 'agents:write',
  },
  Conversations: {
    READ: 'conversations:read',
    WRITE: 'conversations:write',
  },
  Workflows: {
    READ: 'workflows:read',
    WRITE: 'workflows:write',
  },
  Reports: {
    READ: 'reports:read',
  },
  Webhooks: {
    READ: 'webhooks:read',
    WRITE: 'webhooks:write',
  },
} as const;

// ============================================================================
// DERIVED TYPES
// ============================================================================

/** Flat array of every scope string */
export const ALL_API_SCOPES = Object.values(API_SCOPES).flatMap((domain) => Object.values(domain));

/** Union type of all valid scope strings */
export type ApiScope = (typeof ALL_API_SCOPES)[number];

// ============================================================================
// SCOPE PRESETS
// ============================================================================

/** Preset identifier */
export type ScopePresetId = 'READ_ONLY' | 'STANDARD' | 'FULL_ACCESS';

export interface ScopePreset {
  id: ScopePresetId;
  label: string;
  description: string;
  scopes: ApiScope[];
}

/** Pre-defined scope bundles for common API key configurations */
export const API_KEY_SCOPE_PRESETS: Record<ScopePresetId, ScopePreset> = {
  READ_ONLY: {
    id: 'READ_ONLY',
    label: 'Read Only',
    description: 'View cases, consumers, documents, and reports.',
    scopes: [
      API_SCOPES.Cases.READ,
      API_SCOPES.Consumers.READ,
      API_SCOPES.Documents.READ,
      API_SCOPES.Tasks.READ,
      API_SCOPES.Workflows.READ,
      API_SCOPES.Reports.READ,
    ],
  },
  STANDARD: {
    id: 'STANDARD',
    label: 'Standard',
    description: 'Read and write access to core domains: Cases, Consumers, Documents, and Tasks.',
    scopes: [
      API_SCOPES.Cases.READ,
      API_SCOPES.Cases.WRITE,
      API_SCOPES.Consumers.READ,
      API_SCOPES.Consumers.WRITE,
      API_SCOPES.Documents.READ,
      API_SCOPES.Documents.WRITE,
      API_SCOPES.Tasks.READ,
      API_SCOPES.Tasks.WRITE,
      API_SCOPES.Workflows.READ,
    ],
  },
  FULL_ACCESS: {
    id: 'FULL_ACCESS',
    label: 'Full Access',
    description: 'Read and write access to all resources.',
    scopes: ALL_API_SCOPES as ApiScope[],
  },
};

// ============================================================================
// MCP TOOL MANIFEST
// ============================================================================

export interface McpToolDefinition {
  name: string;
  description: string;
  scope: ApiScope;
}

/** Maps each MCP tool to its required scope */
export const MCP_TOOL_MANIFEST: McpToolDefinition[] = [
  // Cases
  { name: 'search_cases', description: 'Search and filter cases', scope: API_SCOPES.Cases.READ },
  { name: 'get_case', description: 'Get case details by ID', scope: API_SCOPES.Cases.READ },
  { name: 'create_case', description: 'Create a new case', scope: API_SCOPES.Cases.WRITE },
  { name: 'update_case', description: 'Update an existing case', scope: API_SCOPES.Cases.WRITE },

  // Consumers
  {
    name: 'search_consumers',
    description: 'Search and filter consumers',
    scope: API_SCOPES.Consumers.READ,
  },
  {
    name: 'get_consumer',
    description: 'Get consumer details by ID',
    scope: API_SCOPES.Consumers.READ,
  },
  {
    name: 'create_consumer',
    description: 'Create a new consumer',
    scope: API_SCOPES.Consumers.WRITE,
  },

  // Documents
  {
    name: 'list_documents',
    description: 'List documents for a case or consumer',
    scope: API_SCOPES.Documents.READ,
  },
  {
    name: 'upload_document',
    description: 'Upload a new document',
    scope: API_SCOPES.Documents.WRITE,
  },

  // Tasks
  { name: 'list_tasks', description: 'List tasks with filters', scope: API_SCOPES.Tasks.READ },
  { name: 'create_task', description: 'Create a new task', scope: API_SCOPES.Tasks.WRITE },

  // Workflows
  {
    name: 'run_workflow',
    description: 'Trigger a workflow execution',
    scope: API_SCOPES.Workflows.WRITE,
  },

  // Reports
  {
    name: 'get_report',
    description: 'Generate and retrieve a report',
    scope: API_SCOPES.Reports.READ,
  },
];

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Filter MCP tools to only those whose required scope is present in the given scopes.
 */
export function getToolsForScopes(scopes: ApiScope[]): McpToolDefinition[] {
  const scopeSet = new Set<string>(scopes);
  return MCP_TOOL_MANIFEST.filter((tool) => scopeSet.has(tool.scope));
}
